import { useState, useMemo } from 'react';
import { SignatureModel } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import SharedSignatureTemplateGridLogs from './shared-signature-template-grid-logs';
import HtmlPreview from '../../../shared/html-preview';
import { useStore } from '../../state/zustand-store';
import DeleteSignatureTemplateDialog from './delete-signature-template-dialog';
import { useSignatureTemplateActions } from './hooks/use-signature-template-actions';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAllSignatures, UserKeys } from '../../../api/users-api';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import { TargetType } from '../../../shared/shared-review-layout-item';
import SharedCard from '../../../shared/shared-card';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Delete from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';

function mapSourceTypeToTargetType(sourceType: string | undefined): TargetType {
  if (!sourceType) return 'Unknown';
  switch (sourceType.toUpperCase()) {
    case 'USER':
      return 'User';
    case 'GROUP':
      return 'Group';
    case 'ORGUNIT':
      return 'OrgUnit';
    case 'REGEX':
      return 'RegEx';
    case 'RESOURCE_CALENDAR':
      return 'Resource Calendar';
    case 'SHARED_DRIVE':
      return 'Shared Drive';
    default:
      return 'User';
  }
}

interface SignatureTemplateDrawerContentProps {
  selectedTemplate: SignatureModel;
}

function SignatureTemplateDrawerContent({
  selectedTemplate,
}: SignatureTemplateDrawerContentProps) {
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const theme = useTheme();
  const { setSnackbar } = useStore();

  const formatDate = (date: string | number | null | undefined) => {
    if (!date || !dayjs(date).isValid()) return 'Never';
    return dayjs(date).format('MMM D, YYYY h:mm A');
  };

  const leftSections: ReviewSection[] = useMemo(() => {
    const emailsToNotify = selectedTemplate.emailsToNotify || [];
    const hasEmails = emailsToNotify.length > 0;

    const sections: ReviewSection[] = [
      {
        title: 'Basic Information',
        leftWidth: {
          labelWidth: 7,
          valueWidth: 5,
        },
        leftItems: [
          {
            label: 'Manual Run:',
            value: !!selectedTemplate.manualRun,
          },
          {
            label: 'Scheduled Run:',
            value: !!selectedTemplate.scheduled,
          },
          {
            label: 'Apply to Only Primary Send As Address:',
            value: !!selectedTemplate.runOnPrimaryAlias,
          },
          {
            label: 'Use send as account for signature template:',
            value: !!selectedTemplate.useSendAsAccountForTemplate,
          },
          {
            label: 'Emails to Notify:',
            value: hasEmails
              ? emailsToNotify.map((email) => ({
                  email,
                  type: 'User' as TargetType,
                }))
              : 'N/A',
            variant: hasEmails ? 'chip' : 'default',
            flex: hasEmails ? true : false,
          },
        ],
      },
    ];

    const sourceTypeDisplay =
      selectedTemplate.sourceType
        ?.replaceAll('_', ' ')
        ?.replace(/([A-Z])/g, ' $1')
        .trim() || 'N/A';

    const sources = selectedTemplate.sources || [];
    const hasSources = sources.length > 0;
    const mappedType = mapSourceTypeToTargetType(selectedTemplate.sourceType);

    sections.push({
      title: 'Targets',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: [
        {
          label: 'Target Type:',
          value: sourceTypeDisplay,
        },
        {
          label: 'Target(s):',
          value: hasSources
            ? sources.map((source) => ({
                email: source,
                type: mappedType,
              }))
            : 'N/A',
          variant: hasSources ? 'chip' : 'default',
          flex: sources && sources.length > 1 ? true : false,
        },
      ],
    });

    if (selectedTemplate.scheduled) {
      const times = selectedTemplate.times || [];
      const dates = selectedTemplate.dates || [];
      const endDate = selectedTemplate.scheduleEndDate;

      const formattedTimes = times.map((time) => {
        const t = Number.parseInt(time, 10);
        if (t === 0) return '12 AM';
        if (t < 12) return `${t} AM`;
        if (t === 12) return '12 PM';
        return `${t - 12} PM`;
      });

      sections.push({
        title: 'Schedule',
        leftWidth: {
          labelWidth: 5,
          valueWidth: 7,
        },
        leftItems: [
          {
            label: 'Times:',
            value:
              formattedTimes.length > 0 ? formattedTimes.join(', ') : 'N/A',
          },
          {
            label: 'Dates:',
            value: dates.length > 0 ? dates.join(', ') : 'N/A',
          },
          {
            label: 'Expiration Date:',
            value: formatDate(endDate),
          },
        ],
      });
    }

    return sections;
  }, [selectedTemplate]);

  const mainSection: MainSectionItem = useMemo(() => {
    return {
      mainTitle: selectedTemplate.templateName || 'N/A',
      description: selectedTemplate.description || 'No description provided.',
      fields: [
        {
          label: 'Last Run Date',
          value: formatDate(selectedTemplate.lastRunDate),
          icon: Update,
        },
        {
          label: 'Created Date',
          value: formatDate(selectedTemplate.createdDate),
          icon: Check,
        },
      ],
    };
  }, [selectedTemplate]);

  return (
    <>
      <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />

      <SharedCard>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="signature template details tabs"
          >
            <Tab label="Overview" />
            <Tab label="Logs" />
          </Tabs>
        </Box>

        <Box
          sx={{
            pt: 2,
            pb: 4,
            px: 2,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          {tabValue === 0 && (
            <Stack spacing={2}>
              <SharedReviewLayoutNew
                leftSections={leftSections}
                isDrawer={true}
                variant="light"
              />
              {selectedTemplate.templateHtml && (
                <Box sx={{ mt: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      display: 'flex',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 'bold', fontSize: 14 }}
                    >
                      Template Preview
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();

                          if (navigator.clipboard) {
                            await navigator.clipboard.writeText(
                              selectedTemplate.templateHtml || ''
                            );
                            setSnackbar({
                              open: true,
                              severity: 'info',
                              message:
                                'Template Html has been copied to clipboard.',
                            });
                          }
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <HtmlPreview
                    html={selectedTemplate.templateHtml}
                    showTitle={false}
                    minHeight={1}
                    sx={{ backgroundColor: theme.palette.semantic.surface.bg3 }}
                  />
                </Box>
              )}
            </Stack>
          )}
          {tabValue === 1 && (
            <SharedSignatureTemplateGridLogs
              signatureId={selectedTemplate.id}
            />
          )}
        </Box>
      </SharedCard>
    </>
  );
}

/**
 * Global, self-contained signature template detail drawer.
 * Driven entirely by the `?selectedTemplate=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function SignatureTemplateDrawerDetails() {
  const [searchParams] = useSearchParams();
  const { toolsRoles, changeLogRoles } = useUserSettings();
  const {
    closeDrawer,
    handleEditTemplate,
    handleCloneTemplate,
    handleDeleteTemplate,
    openDeleteDialog,
    closeDeleteDialog,
    templateForDelete,
  } = useSignatureTemplateActions();

  const selectedTemplateId = searchParams.get('selectedTemplate');
  const { data: signatures, isLoading } = useQuery({
    queryKey: [UserKeys.Signatures],
    queryFn: () => getAllSignatures(),
    enabled: !!selectedTemplateId,
  });

  const selectedSignatureTemplate =
    signatures?.find((s) => s.id.toString() === selectedTemplateId) ?? null;

  const isDrawerOpen = !!selectedTemplateId;

  const canEditUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditTemplate.value
  );
  const canCloneUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CreateTemplates.value
  );
  const canDeleteUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeleteTemplates.value
  );

  const editDisabledReason = canEditUI
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.EditTemplate.name);
  const cloneDisabledReason = canCloneUI
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.CreateTemplates.name);
  const deleteDisabledReason = canDeleteUI
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.DeleteTemplates.name);

  const actionsMenu: SimpleDrawerAction[] | undefined =
    selectedSignatureTemplate
      ? [
          {
            label: 'Edit',
            icon: <Edit fontSize="small" />,
            onClick: () => handleEditTemplate(selectedSignatureTemplate),
            disabled: !canEditUI,
            disabledReason: editDisabledReason,
          },
          {
            label: 'Clone',
            icon: <LibraryAdd fontSize="small" />,
            onClick: () => handleCloneTemplate(selectedSignatureTemplate),
            disabled: !canCloneUI,
            disabledReason: cloneDisabledReason,
          },
          {
            label: 'Delete',
            icon: <Delete fontSize="small" />,
            onClick: () => handleDeleteTemplate(selectedSignatureTemplate),
            disabled: !canDeleteUI,
            disabledReason: deleteDisabledReason,
          },
        ]
      : undefined;

  return (
    <>
      <SimpleDrawer
        title="Signature Template Details"
        open={isDrawerOpen}
        onClose={closeDrawer}
        isLoading={isLoading}
        actionsMenu={actionsMenu}
      >
        {selectedSignatureTemplate ? (
          <SignatureTemplateDrawerContent
            selectedTemplate={selectedSignatureTemplate}
          />
        ) : (
          !isLoading && (
            <Typography sx={{ p: 3 }} color="error">
              Template not found.
            </Typography>
          )
        )}
      </SimpleDrawer>

      {templateForDelete && openDeleteDialog && (
        <DeleteSignatureTemplateDialog
          open={openDeleteDialog}
          handleClose={() => {
            closeDeleteDialog();
          }}
          selectedRows={[templateForDelete.id]}
          setSelectedRows={() => {}}
          onDeleteSuccess={() => {
            closeDrawer();
          }}
          changeLogRoles={changeLogRoles}
          signatures={[templateForDelete]}
        />
      )}
    </>
  );
}
